import React, { useEffect, useState, useCallback } from "react";
import { api, formatBRL } from "../lib/api";
import { fetchWithV2Fallback } from "../lib/v2";
import { Users, Plus, RefreshCw, AlertCircle, Pencil, Archive, Check, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

export default function Payers() {
  const [payers, setPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ cnpj: "", legal_name: "", email: "", phone: "", city: "", state: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWithV2Fallback("/v2/payers", null);
      const list = result.data?.items || (Array.isArray(result.data) ? result.data : []);
      setPayers(list);
    } catch {
      setError("Erro ao carregar sacados.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.cnpj || !form.legal_name) return;
    setSubmitting(true);
    try {
      const body = {};
      for (const [k, v] of Object.entries(form)) { if (v) body[k] = v; }
      await api.post("/v2/payers", body);
      toast.success("Sacado cadastrado");
      setShowForm(false);
      setForm({ cnpj: "", legal_name: "", email: "", phone: "", city: "", state: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar sacado");
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (id, name) => {
    if (!window.confirm(`Arquivar sacado "${name}"?`)) return;
    try {
      await api.delete(`/v2/payers/${id}`);
      toast.success("Sacado arquivado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao arquivar");
    }
  };

  return (
    <div className="space-y-6" data-testid="payers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Gestao</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">Sacados</h1>
          <p className="mt-1 text-sm text-zinc-500">Devedores dos recebiveis — quem paga as notas/titulos.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="payers-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="payers-new-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Novo sacado
          </Button>
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="zemo-card p-5 space-y-4" data-testid="payer-create-form">
          <h3 className="text-sm font-semibold">Cadastrar sacado</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-[12px]">CNPJ/CPF *</Label>
              <Input value={form.cnpj} onChange={(e) => setForm(p => ({...p, cnpj: e.target.value}))} placeholder="11.222.333/0001-99" className="h-9" data-testid="payer-cnpj" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-[12px]">Razao social *</Label>
              <Input value={form.legal_name} onChange={(e) => setForm(p => ({...p, legal_name: e.target.value}))} placeholder="Empresa XYZ Ltda" className="h-9" data-testid="payer-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-[12px]">Email</Label>
              <Input value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} placeholder="financeiro@xyz.com" className="h-9" data-testid="payer-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-[12px]">Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm(p => ({...p, phone: e.target.value}))} placeholder="11999998888" className="h-9" data-testid="payer-phone" />
            </div>
            <div className="space-y-1">
              <Label className="text-[12px]">Cidade / UF</Label>
              <div className="flex gap-2">
                <Input value={form.city} onChange={(e) => setForm(p => ({...p, city: e.target.value}))} placeholder="Sao Paulo" className="h-9 flex-1" />
                <Input value={form.state} onChange={(e) => setForm(p => ({...p, state: e.target.value}))} placeholder="SP" className="h-9 w-16" maxLength={2} />
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}><X className="h-3.5 w-3.5 mr-1" /> Cancelar</Button>
            <Button type="submit" size="sm" disabled={submitting || !form.cnpj || !form.legal_name} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="payer-submit">
              <Check className="h-3.5 w-3.5 mr-1" /> {submitting ? "Salvando..." : "Cadastrar"}
            </Button>
          </div>
        </form>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="payers-error">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : payers.length === 0 ? (
        <div className="zemo-card p-12 text-center" data-testid="payers-empty">
          <Users className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Nenhum sacado cadastrado</p>
          <p className="text-xs text-zinc-400 mt-1">Sacados sao criados automaticamente ao criar operacoes, ou cadastre manualmente.</p>
        </div>
      ) : (
        <div className="zemo-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Razao Social</th>
                  <th className="text-left px-5 py-3 font-semibold">CNPJ/CPF</th>
                  <th className="text-left px-5 py-3 font-semibold">Email</th>
                  <th className="text-left px-5 py-3 font-semibold">Cidade/UF</th>
                  <th className="text-left px-5 py-3 font-semibold">Auto-aprovar</th>
                  <th className="text-right px-5 py-3 font-semibold">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--zemo-border)]" data-testid="payers-table">
                {payers.map((p) => (
                  <tr key={p.id} className="hover:bg-zinc-50/60">
                    <td className="px-5 py-3.5 font-semibold text-zinc-900">{p.legal_name}</td>
                    <td className="px-5 py-3.5 font-mono-num text-zinc-600">{p.cnpj}</td>
                    <td className="px-5 py-3.5 text-zinc-600">{p.email || "--"}</td>
                    <td className="px-5 py-3.5 text-zinc-600">{[p.city, p.state].filter(Boolean).join("/") || "--"}</td>
                    <td className="px-5 py-3.5">
                      {p.approve_automatic ? (
                        <span className="text-emerald-600 text-xs font-semibold">Sim</span>
                      ) : (
                        <span className="text-zinc-400 text-xs">Nao</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => handleArchive(p.id, p.legal_name)}
                        className="text-zinc-400 hover:text-red-500 transition-colors"
                        title="Arquivar sacado"
                        data-testid={`archive-payer-${p.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
